import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Avatar,
  SimpleGrid,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * Workshop Testimonials Section
 * Displays student testimonials and success stories
 */
const WorkshopTestimonials = ({ workshop }) => {
  const gridCols = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  if (!workshop?.testimonials || workshop.testimonials.length === 0) return null;

  const { testimonials } = workshop;

  return (
    <Box py={{ base: 16, md: 20 }} bg="gray.50">
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          mb={{ base: 12, md: 16 }}
        >
          <VStack align="center" spacing={3} textAlign="center">
            <Heading as="h2" size="xl" fontWeight="900" color="gray.900">
              What Attendees Say
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Real feedback from past workshop participants
            </Text>
          </VStack>
        </MotionBox>

        {/* Testimonials Grid */}
        <SimpleGrid columns={gridCols} spacing={8}>
          {testimonials.map((testimonial, idx) => (
            <MotionBox
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              <Box
                bg="white"
                p={8}
                borderRadius="lg"
                h="full"
                display="flex"
                flexDirection="column"
                boxShadow="sm"
              >
                {/* Rating */}
                {testimonial.rating && (
                  <Box mb={4}>
                    <Text fontSize="lg">
                      {'⭐'.repeat(testimonial.rating)}
                    </Text>
                  </Box>
                )}

                {/* Quote */}
                <Text
                  fontSize="md"
                  color="gray.700"
                  mb={6}
                  flex={1}
                  lineHeight="1.8"
                  fontStyle="italic"
                >
                  "{testimonial.quote}"
                </Text>

                {/* Author */}
                <HStack spacing={3} mt="auto" pt={6} borderTop="1px solid" borderColor="gray.200">
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" color="gray.900">
                      {testimonial.name}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {testimonial.role}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </MotionBox>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default WorkshopTestimonials;
